import { Check, Copy } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Button, type ButtonProps } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface CopyButtonProps extends Pick<ButtonProps, 'size' | 'className'> {
  value: string;
  /** Accessible label; defaults to "Copy" + the value's kind. */
  label?: string;
}

/**
 * Copy-to-clipboard affordance next to every displayed id (design §3.1 D2/D4 —
 * there is no user-lookup endpoint, so ids must be shareable). Shows "Copied"
 * for 1.5s via its own label swap (aria-live) rather than a tooltip, so it does
 * not depend on a shell-level TooltipProvider.
 */
export function CopyButton({
  value,
  label = 'Copy user ID',
  size = 'sm',
  className,
}: CopyButtonProps) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) {
      return;
    }
    const timer = window.setTimeout(() => setCopied(false), 1500);
    return () => window.clearTimeout(timer);
  }, [copied]);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      // Clipboard is unavailable (insecure context / denied permission): the id
      // stays on screen for manual copying, so no error state is shown.
      setCopied(false);
    }
  }

  return (
    <Button
      type="button"
      variant="ghost"
      size={size}
      onClick={handleCopy}
      aria-label={label}
      className={cn('h-7 w-7 p-0 text-muted-foreground hover:text-foreground', className)}
    >
      {copied ? (
        <Check className="h-3.5 w-3.5" aria-hidden="true" />
      ) : (
        <Copy className="h-3.5 w-3.5" aria-hidden="true" />
      )}
      <span className="sr-only" role="status" aria-live="polite">
        {copied ? 'Copied' : ''}
      </span>
    </Button>
  );
}
